"use client";

import { useRegion } from "@/utils/hooks/context/region";
import { provinceOptions } from "@/app/helper/constants";
import { useEffect, useState } from "react";
import { Dialog, DialogHeader, DialogBody } from "@dxp-feed/ui/Dialog";
import { Select } from "@dxp-feed/ui/Select";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { addDataLayer } from "@/helper/addDataLayer";
import { Button } from "@dxp-feed/ui/Button";
import { useLang } from "@/utils/hooks/context/lang";
import { BINDING_CONFIG } from "@/utils/constants";

export default function SelectRegionOverlay({
  bindedDomain,
}: {
  bindedDomain: keyof typeof BINDING_CONFIG;
}) {
  const { region, setRegion } = useRegion();
  const { setLang } = useLang();
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const router = useRouter();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDataLayer = (selectedRegion: string) => {
    if (["/register"].includes(pathName)) {
      addDataLayer({
        event: "sign_up",
        param2: "step_1",
        param1: selectedRegion,
        param4: document.title ?? "Sobeys SSO",
      });
      addDataLayer({
        event: "sign_up",
        param2: "step_2",
        param1: BINDING_CONFIG[bindedDomain].BANNER || "",
        param4: document.title ?? "Sobeys SSO",
      });
    }
  };

  useEffect(() => {
    if (!["/login", "/register"].includes(pathName)) {
      router.push("/login");
    }
  }, [pathName]);

  useEffect(() => {
    const gigRegion = searchParams.get("gig_region");
    const gigLang = searchParams.get("gig_locale");
    const gigRegionParam = provinceOptions.map(doc => doc.optionname).includes(gigRegion as string) ? gigRegion : null;
    const gigLangParam = (process.env.NEXT_PUBLIC_LANG_OPTIONS as string).split(",").includes(gigLang as string) ? gigLang : BINDING_CONFIG[bindedDomain].LOCALE
    const localRegion = localStorage.getItem("region");

    localStorage.setItem("banner", BINDING_CONFIG[bindedDomain].BANNER || "");

    if (gigLangParam) {
      setLang(gigLangParam);
    }

    if (gigRegionParam) {
      setRegion(gigRegionParam);
      localStorage.setItem("region", gigRegionParam);
      handleDataLayer(gigRegionParam);
    } else if (!gigRegionParam && localRegion) {
      setRegion(localRegion);
      handleDataLayer(localRegion);
    } else if (!gigRegionParam && !localRegion) {
      setIsModalOpen(true);
    }
  }, [searchParams]);

  // Handle province selection change
  const handleChange = (value: string) => {
    localStorage.setItem("region", value);
    setRegion(value);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!region) return; // Guard against empty submission
    handleDataLayer(region);
    setIsModalOpen(false); // Close the modal
  };

  return (
    <Dialog
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      aria-labelledby="modal-header"
      aria-describedby="modal-body"
      className="p-4 px-6 outline-none xs:w-[397px] w-[90%]"
      closeOnOverlayClick={false}
    >
      <DialogHeader>
        <h2 id="modal-header" className="text-lg font-bold">
          Select your preferred province
        </h2>
      </DialogHeader>
      <DialogBody className="pb-0">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Dropdown for province selection */}
          <div>
            <Select
              options={provinceOptions}
              onChange={handleChange}
              defaultValue={""} // Set the default value to match the state
              defaultLabel="Select an option"
              icon={
                <span className="select-arrow w-6 h-3 block bg-brandWhite"></span>
              } // Optional custom icon
              className="[&_button>span]:text-black [&_button>span]:text-base [&_button]:border-grey350 [&_ul>li]:h-14 [&_ul]:md:max-h-[340px] [&_ul]:max-h-[290px]"
              scroll
              checked
            />
          </div>

          {/* Submit button */}
          <Button
            variant="primary"
            rounded="full"
            type="submit"
            className="preferredPopbutton w-auto"
          >
            Submit
          </Button>
        </form>
      </DialogBody>
    </Dialog>
  );
}
